import { FunctionalComponent } from "preact";
import { useState, useEffect } from "preact/hooks";
import { api } from "../utils/api";

interface ReadingViewProps {
  bookmarkId: string;
  onClose: () => void;
}

export const ReadingView: FunctionalComponent<ReadingViewProps> = ({
  bookmarkId,
  onClose,
}) => {
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metadata, setMetadata] = useState<{
    url: string;
    title: string;
    archivedAt: string;
    contentType: string;
    size: number;
  } | null>(null);

  useEffect(() => {
    const loadArchive = async () => {
      try {
        setLoading(true);
        const response = await api.getArchivedContent(bookmarkId);
        setContent(response.content);
        setMetadata(response.metadata);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load archive");
      } finally {
        setLoading(false);
      }
    };
    loadArchive();
  }, [bookmarkId]);

  if (loading) {
    return (
      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50" onClick={onClose} />
          <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div class="p-8 text-center">
              <div class="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p class="text-gray-500 dark:text-gray-400">Loading archived content...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-screen items-center justify-center p-4">
          <div class="fixed inset-0 bg-black/50" onClick={onClose} />
          <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div class="text-center">
              <svg class="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">Failed to Load Archive</h3>
              <p class="text-gray-500 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={onClose}
                class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex min-h-screen items-center justify-center p-4">
        <div class="fixed inset-0 bg-black/50" onClick={onClose} />
        <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div class="flex-1 min-w-0 pr-4">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {metadata?.title || "Archived Page"}
              </h2>
              <a
                href={metadata?.url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-primary-500 hover:underline truncate block"
              >
                {metadata?.url}
              </a>
            </div>
            <button
              onClick={onClose}
              class="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Meta info bar */}
          {metadata && (
            <div class="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Archived: {new Date(metadata.archivedAt).toLocaleDateString()}</span>
              <span>Size: {(metadata.size / 1024).toFixed(1)} KB</span>
              <a
                href={metadata.url}
                target="_blank"
                rel="noopener noreferrer"
                class="ml-auto text-primary-500 hover:underline flex items-center gap-1"
              >
                <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open Original
              </a>
            </div>
          )}

          {/* Content */}
          <div class="flex-1 overflow-auto">
            <iframe
              srcDoc={content}
              class="w-full h-full min-h-[500px] border-0"
              sandbox="allow-same-origin"
              title="Archived content"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
